/**
 * features/hr/useHrData.ts
 * ------------------------------------------------------------------
 * Live data layer for the HR DOMAIN ONLY. Subscribes to the hr*
 * collections and exposes them to the HR screens. Deliberately
 * separate from the academic DataContext so the two domains stay
 * isolated — this hook never reads reportStudents / reportMarks /
 * reportAttendance, and the academic DataContext never reads hr*.
 *
 * The single bridge (resolving a teaching staff member's academic
 * teacher record) is done by reading the shared academic
 * DataContext's `teachers` array and matching on linkedTeacherId —
 * a read-only lookup, never a write across the boundary.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { ENCASHMENT_COLLECTION, type HrLeaveEncashment } from '@/lib/leaveEncashment';
import {
  HR_COLLECTIONS,
  type HrStaff,
  type HrComplianceDoc,
  type HrComplianceMatrixEntry,
  type HrAttendance,
  type HrLeaveRequest,
  type HrContract,
  type HrAppraisal,
  type HrPayrollRef,
} from '@/lib/hr';
import {
  PAYROLL_COLLECTIONS,
  type HrPayrollProfile,
  type HrDeduction,
  type AllowanceCatalogEntry,
  type HrPayrollSettings,
  type HrTaxTable,
  type HrLeaveAllocation,
  type HrPayslip,
  type HrGratuityPayout,
} from '@/lib/hrPayroll';

interface HrData {
  staff: HrStaff[];
  complianceDocs: HrComplianceDoc[];
  complianceMatrix: HrComplianceMatrixEntry[];
  attendance: HrAttendance[];
  leave: HrLeaveRequest[];
  contracts: HrContract[];
  appraisals: HrAppraisal[];
  payrollRefs: HrPayrollRef[];
  payrollProfiles: HrPayrollProfile[];
  deductions: HrDeduction[];
  allowanceCatalog: AllowanceCatalogEntry[];
  payrollSettings?: HrPayrollSettings;
  taxTables: HrTaxTable[];
  activeTaxTable?: HrTaxTable;
  leaveAllocations: HrLeaveAllocation[];
  /** Sales of leave days back to the school. */
  leaveEncashments: HrLeaveEncashment[];
  payslips: HrPayslip[];
  gratuityPayouts: HrGratuityPayout[];
  loading: boolean;
  error: string | null;
  staffById: (id: string) => HrStaff | undefined;
}

function useCollection<T>(name: string): { rows: T[]; ready: boolean; err: string | null } {
  const [rows, setRows] = useState<T[]>([]);
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const q = query(collection(db, name));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as T));
        setReady(true);
      },
      (e) => {
        setErr(e.message);
        setReady(true);
      },
    );
    return unsub;
  }, [name]);

  return { rows, ready, err };
}

/**
 * Only what the Clocking screen draws. It is opened by the clocking uploader as
 * well as by HR, and the uploader is refused the rest of the HR domain, so this
 * must not go through useHrData. Approved leave only: that is all the sheet
 * shows, and all the rules let the uploader read (the query has to say so).
 */
export function useClockingData() {
  const staff = useCollection<HrStaff>(HR_COLLECTIONS.staff);
  const attendance = useCollection<HrAttendance>(HR_COLLECTIONS.attendance);
  const [leave, setLeave] = useState<HrLeaveRequest[]>([]);
  const [leaveReady, setLeaveReady] = useState(false);
  useEffect(() => onSnapshot(
    query(collection(db, HR_COLLECTIONS.leave), where('status', '==', 'approved')),
    (snap) => {
      setLeave(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as HrLeaveRequest));
      setLeaveReady(true);
    },
    () => setLeaveReady(true),
  ), []);
  return {
    staff: staff.rows,
    attendance: attendance.rows,
    leave,
    loading: !staff.ready || !attendance.ready || !leaveReady,
  };
}

export function useHrData(): HrData {
  const staff = useCollection<HrStaff>(HR_COLLECTIONS.staff);
  const docs = useCollection<HrComplianceDoc>(HR_COLLECTIONS.complianceDocs);
  const matrix = useCollection<HrComplianceMatrixEntry>(
    HR_COLLECTIONS.complianceMatrix,
  );
  const attendance = useCollection<HrAttendance>(HR_COLLECTIONS.attendance);
  const leave = useCollection<HrLeaveRequest>(HR_COLLECTIONS.leave);
  const contracts = useCollection<HrContract>(HR_COLLECTIONS.contracts);
  const appraisals = useCollection<HrAppraisal>(HR_COLLECTIONS.appraisals);
  const payrollRefs = useCollection<HrPayrollRef>(HR_COLLECTIONS.payrollRefs);
  const payrollProfiles = useCollection<HrPayrollProfile>(
    PAYROLL_COLLECTIONS.payrollProfile,
  );
  const deductions = useCollection<HrDeduction>(PAYROLL_COLLECTIONS.deductions);
  const allowanceCatalog = useCollection<AllowanceCatalogEntry>(
    PAYROLL_COLLECTIONS.allowanceCatalog,
  );
  const payrollSettings = useCollection<HrPayrollSettings>(
    PAYROLL_COLLECTIONS.settings,
  );
  const taxTables = useCollection<HrTaxTable>(PAYROLL_COLLECTIONS.taxTables);
  const leaveAllocations = useCollection<HrLeaveAllocation>(
    PAYROLL_COLLECTIONS.leaveAllocations,
  );
  const payslips = useCollection<HrPayslip>(PAYROLL_COLLECTIONS.payslips);
  /* DELIBERATELY NOT IN THE LOADING GATE BELOW. Every HR screen waits on
     that gate, and a sale only adjusts a balance already built from the
     rest. If this collection cannot be read — a rules deploy that has not
     landed, a role without access — the leave module must still open. */
  const leaveEncashments = useCollection<HrLeaveEncashment>(ENCASHMENT_COLLECTION);
  const gratuityPayouts = useCollection<HrGratuityPayout>(PAYROLL_COLLECTIONS.gratuityPayouts);

  const loading =
    !staff.ready ||
    !docs.ready ||
    !matrix.ready ||
    !attendance.ready ||
    !leave.ready ||
    !contracts.ready ||
    !appraisals.ready ||
    !payrollRefs.ready ||
    !payrollProfiles.ready ||
    !deductions.ready ||
    !allowanceCatalog.ready ||
    !payrollSettings.ready ||
    !taxTables.ready ||
    !leaveAllocations.ready ||
    !payslips.ready ||
    !gratuityPayouts.ready;

  const error =
    staff.err ||
    docs.err ||
    matrix.err ||
    attendance.err ||
    leave.err ||
    contracts.err ||
    appraisals.err ||
    payrollRefs.err ||
    payrollProfiles.err ||
    deductions.err ||
    allowanceCatalog.err ||
    payrollSettings.err ||
    taxTables.err ||
    leaveAllocations.err ||
    payslips.err ||
    gratuityPayouts.err;

  const staffById = useMemo(() => {
    const map = new Map(staff.rows.map((s) => [s.id, s]));
    return (id: string) => map.get(id);
  }, [staff.rows]);

  return {
    staff: staff.rows,
    complianceDocs: docs.rows,
    complianceMatrix: matrix.rows,
    attendance: attendance.rows,
    leave: leave.rows,
    contracts: contracts.rows,
    appraisals: appraisals.rows,
    payrollRefs: payrollRefs.rows,
    payrollProfiles: payrollProfiles.rows,
    deductions: deductions.rows,
    allowanceCatalog: allowanceCatalog.rows,
    payrollSettings: payrollSettings.rows[0],
    taxTables: taxTables.rows,
    activeTaxTable: taxTables.rows.find((t) => t.active),
    leaveAllocations: leaveAllocations.rows,
    leaveEncashments: leaveEncashments.rows,
    payslips: payslips.rows,
    gratuityPayouts: gratuityPayouts.rows,
    loading,
    error,
    staffById,
  };
}
